import type { PackagingItemSvhcAuditStatus } from './packagingItemSvhcAuditOverviewClient'
import type { PackagingSvhcAuditVerdict } from '@/shared/hooks/api/packagingSuppliers'
import { svhcAuditVerdictToOverviewStatus } from './packagingItemSvhcAuditOverviewClient'

export const PACKAGING_ITEMS_OVERVIEW_SVHC_AUDIT_STATUS_FILTER_VALUES = [
  'not_audited',
  'passed',
  'failed',
  'flagged',
] as const satisfies readonly PackagingItemSvhcAuditStatus[]

export const PACKAGING_ITEMS_OVERVIEW_SVHC_AUDIT_VERDICT_FILTER_VALUES = [
  'SVHC_TEST_REPORT_SVHC_NEGATIVE',
  'SVHC_TEST_REPORT_SVHC_POSITIVE',
  'SVHC_TEST_REPORT_INCONCLUSIVE',
  'SVHC_TEST_REPORT_NOT_PRESENT',
] as const satisfies readonly PackagingSvhcAuditVerdict[]

export const PACKAGING_ITEMS_OVERVIEW_SVHC_AUDIT_FILTER_VALUES = [
  ...PACKAGING_ITEMS_OVERVIEW_SVHC_AUDIT_STATUS_FILTER_VALUES,
  ...PACKAGING_ITEMS_OVERVIEW_SVHC_AUDIT_VERDICT_FILTER_VALUES,
] as const

export type PackagingItemSvhcAuditFilterValue =
  (typeof PACKAGING_ITEMS_OVERVIEW_SVHC_AUDIT_FILTER_VALUES)[number]

const SVHC_AUDIT_STATUS_FILTER_SET = new Set<string>(
  PACKAGING_ITEMS_OVERVIEW_SVHC_AUDIT_STATUS_FILTER_VALUES
)

export function isPackagingItemSvhcAuditStatusFilterValue(
  value: string
): value is PackagingItemSvhcAuditStatus {
  return SVHC_AUDIT_STATUS_FILTER_SET.has(value)
}

export function packagingItemMatchesSvhcAuditFilterValue(
  input: {
    svhcAuditStatus: PackagingItemSvhcAuditStatus
    svhcAuditLatest: { verdict: PackagingSvhcAuditVerdict } | null
  },
  filterValues: readonly string[]
): boolean {
  if (filterValues.length === 0) {
    return true
  }
  return filterValues.some((value) => {
    if (isPackagingItemSvhcAuditStatusFilterValue(value)) {
      return input.svhcAuditStatus === value
    }
    if (input.svhcAuditLatest == null) {
      return false
    }
    return input.svhcAuditLatest.verdict === value
  })
}

export function packagingItemSvhcAuditStatusLabelKey(
  status: PackagingItemSvhcAuditStatus
): `list.svhcAudit.status.${PackagingItemSvhcAuditStatus}` {
  return `list.svhcAudit.status.${status}`
}

export function packagingSvhcAuditVerdictLabelKey(
  verdict: PackagingSvhcAuditVerdict
): `itemDetail.svhcAudit.verdicts.${PackagingSvhcAuditVerdict}` {
  return `itemDetail.svhcAudit.verdicts.${verdict}`
}

const SVHC_AUDIT_VERDICT_ROLLUP_ORDER: Record<
  PackagingItemSvhcAuditStatus,
  number
> = {
  passed: 0,
  failed: 1,
  flagged: 2,
  not_audited: 3,
}

export function compareSvhcAuditVerdictFilterValues(
  left: PackagingSvhcAuditVerdict,
  right: PackagingSvhcAuditVerdict
): number {
  const leftOrder =
    SVHC_AUDIT_VERDICT_ROLLUP_ORDER[svhcAuditVerdictToOverviewStatus(left)]
  const rightOrder =
    SVHC_AUDIT_VERDICT_ROLLUP_ORDER[svhcAuditVerdictToOverviewStatus(right)]
  if (leftOrder !== rightOrder) {
    return leftOrder - rightOrder
  }
  return left.localeCompare(right)
}
